export const CHART_PALETTE = ['#417800', '#853431', '#e0eecf', '#99ca55', '#cdd5c4', '#a3cd75'];

export const PALETTE_FINANZIERUNGSTYP = [
  '#417800',
  '#5e9c00',
  '#99ca55',
  '#a3cd75',
  '#cdd5c4',
  '#e0eecf',
];

export const PALETTE_FINANZIERUNGSINSTRUMENT = [
  '#853431',
  '#b04030',
  '#c45c28',
  '#e07840',
  '#f0a060',
  '#fac898',
];

export const PALETTE_BEREICH = [
  '#5a2d82',
  '#7a4ca8',
  '#9a6ec4',
  '#bb96d6',
  '#d9bee8',
  '#f0e4f8',
];

export const PALETTE_INCOMEGROUPS = [
  '#005f6b',
  '#007d8c',
  '#1a9eae',
  '#5cbfcc',
  '#95d8e0',
  '#cbecf0',
];

export function getPaletteForVariant(variant: string): string[] {
  if (variant === '_financingtool' || variant === 'financingtool') return PALETTE_FINANZIERUNGSINSTRUMENT;
  if (variant === '_bereich'       || variant === 'bereich')       return PALETTE_BEREICH;
  if (variant === '_incomegroups'  || variant === 'incomegroups')  return PALETTE_INCOMEGROUPS;
  return PALETTE_FINANZIERUNGSTYP;
}

// Bar chart titles — one per variant. Fill in the strings to activate the title.
const TITLES_BY_VARIANT: Record<string, string> = {
  '':                'Deutsche Klimafinanzierung nach Finanzierungstyp', // Finanzierungstyp
  '_financingtool':  'Deutsche Klimafinanzierung nach Finanzierungsinstrument', // Finanzierungsinstrument
  '_bereich':        'Deutsche Klimafinanzierung nach Bereich', // Bereich
  '_incomegroups':   'Deutsche Klimafinanzierung nach Weltbank-Einkommensgruppe', // Weltbank-Einkommensgruppen
};

export function getTitleForVariant(variant: string): string {
  return TITLES_BY_VARIANT[variant] ?? '';
}

// Pie chart titles — include {year} as a placeholder, replaced at render time.
const PIE_TITLES_BY_VARIANT: Record<string, string> = {
  'bereich':       'Deutsche Klimafinanzierung {year} nach Bereich', // Bereich
  'incomegroups':  'Deutsche Klimafinanzierung {year} nach Weltbank-Einkommensgruppe', // Weltbank-Einkommensgruppen
  'financingtool': 'Deutsche Klimafinanzierung {year} nach Finanzierungsinstrument', // Finanzierungsinstrument
};

export function getPieTitleForVariant(variant: string, year: number): string {
  const template = PIE_TITLES_BY_VARIANT[variant] ?? '';
  return template.replace('{year}', String(year));
}

// Shared descriptions for bar and pie charts — keyed by canonical variant name.
const DESCRIPTIONS_BY_VARIANT: Record<string, string> = {
  'finanzierungstyp': 'Angaben in Milliarden Euro. Mobilisierte öffentliche Mittel sind Mittel, die die KfW auf dem Kapitalmarkt aufnimmt, um damit dann z.B. öffentliche Darlehen zu finanzieren. Mobilisierte private Mittel sind private Investitionen, die die die Bundesregierung durch den Einsatz öffentlicher Mittel mobilisiert hat. Schenkungsäquivalente sind eine rein rechnerische Größe, die z.B. bei einem zinsvergünstigen Darlehen die tatsächliche finanzielle Leistung Deutschlands abbildet. Quelle: Eigene Darstellung auf Basis der Datenbank der Website www.deutscheklimafinanzierung.de nach Daten der Bundesregierung.', // Finanzierungstyp
  'financingtool':    'Angaben in Milliarden Euro. Schenkungsäquivalente sind eine rein rechnerische Größe, die z.B. bei einem zinsvergünstigen Darlehen die tatsächliche finanzielle Leistung Deutschlands abbildet. Quelle: Eigene Darstellung auf Basis der Datenbank der Website www.deutscheklimafinanzierung.de nach Daten der Bundesregierung.', // Finanzierungsinstrument
  'bereich':          'Angaben in Milliarden Euro. „Übergreifend“ bezeichnet Projekte und Programme, die Auffassung der Bundesregierung sowohl der Emissionsminderung als auch der Anpassung an den Klimawandel dienen. Quelle: Eigene Darstellung auf Basis der Datenbank der Website www.deutscheklimafinanzierung.de nach Daten der Bundesregierung.', // Bereich
  'incomegroups':     'Angaben in Milliarden Euro. Quelle: Eigene Darstellung auf Basis der Datenbank der Website www.deutscheklimafinanzierung.de nach Daten der Bundesregierung.', // Weltbank-Einkommensgruppen
};

function toCanonicalVariant(variant: string): string {
  if (variant === '' )               return 'finanzierungstyp';
  if (variant.startsWith('_'))       return variant.slice(1);
  return variant;
}

export function getDescriptionForVariant(variant: string): string {
  return DESCRIPTIONS_BY_VARIANT[toCanonicalVariant(variant)] ?? '';
}

export type ChartTheme = {
  fontFamily: string;
  textColor: string;
  controlColor: string;
  gridColor: string;
  palette: string[];
};

export type ChartMountOptions = {
  endpoint: string;
  height?: number;
  locale?: string;
  chartType?: 'stacked-bar' | 'pie';
};

export function colorFor(label: string): string {
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 65% 55%)`;
}

export function getChartTheme(): ChartTheme {
  if (typeof window === 'undefined') {
    return {
      fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      textColor: '#111111',
      controlColor: '#666666',
      gridColor: 'rgba(0,0,0,0.05)',
      palette: CHART_PALETTE
    };
  }

  const root = document.documentElement;
  const styles = getComputedStyle(root);

  const getVar = (name: string, fallback: string) => {
    const value = styles.getPropertyValue(name).trim();
    return value || fallback;
  };

  return {
    fontFamily: getVar('--ddk-font-family--default', "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"),
    textColor: getVar('--ddk-color--text', '#111111'),
    controlColor: getVar('--ddk-color--gray-submenu', '#666666'),
    gridColor: getVar('--ddk-color--black-4', 'rgba(0,0,0,0.05)'),
    palette: CHART_PALETTE
  };
}